import React, { useState, useEffect } from "react";
import { Box, Button, Typography, Chip, Grid, IconButton, Select, MenuItem, FormControl } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useSelector } from "react-redux";
import axios from "axios";
import Loading from "../../../../common/loader/loading";

const WeeklyFirstAidItemCheck = ({ onClose, selectedTask, setOpenDefaultSnackbar }) => {
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const today = new Date();
  const currentDate = today.toISOString().split("T")[0];
  const [quantity, setQuantity] = useState(1);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [responses, setResponses] = useState([]);
  const [location, setLocation] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state
  const [taskId, setTaskId] = useState(selectedTask?.id);

  useEffect(() => {
    if (!taskId && selectedTask?.id) {
      setTaskId(selectedTask.id);
    }
  }, [selectedTask?.id]); // Run only if `selectedTask.id` is not set

  const updateSubTask = async (taskId) => {
    if (!taskId) {
      console.error("Task ID is undefined. Cannot update.");
      return;
    }

    try {
      await axios.put(
        `/api/assessments/updateTask/${taskId}`,
        {
          status: "completed",
          checklist: [],
          updated_by: user?.username || "Unknown",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-user-role": user?.roleid || "",
          },
        }
      );
      setOpenDefaultSnackbar(true);
    } catch (error) {
      console.error("Error updating subtask:", error);
    }
  };

  useEffect(() => {
    const fetchFilteredData = async () => {
      setLoading(true);
      try {
        const response = await axios.get("/api/firstAid/firstAidItemAll", {
          params: { location },
        });

        const filteredItems = (response.data.items || []).filter((item) => {
          const createdAt = new Date(item.created_at);
          const currentDate = new Date();
          const diffInDays = (currentDate - createdAt) / (1000 * 60 * 60 * 24); // Convert milliseconds to days
          return diffInDays <= 7; // Only include records from the past week
        });

        setFilteredData(filteredItems);
      } catch (err) {
        console.error("Error fetching filtered data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredData();
  }, [location]);

  // Extract firstAids based on selected box and location
  const selectedRecord = filteredData.find((item) => item.location === location);
  const firstAids = selectedRecord?.firstAids.map((item) => (typeof item === "string" ? item : item.firstAid)) || [];

  const handleNext = () => {
    setResponses([
      ...responses,
      {
        firstAid:
          typeof firstAids[currentProductIndex] === "object"
            ? firstAids[currentProductIndex]?.firstAid
            : firstAids[currentProductIndex],
        quantity,
        date: selectedDate,
      },
    ]);

    if (currentProductIndex < firstAids.length - 1) {
      setCurrentProductIndex((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    const payload = {
      firstAids: responses,
      location: location,
      completed_by: user.username,
    };

    try {
      await axios.put(`/api/firstAid/firstAidItem/${selectedRecord?.id}`, payload);

      if (taskId && availableLocations.length === 1) {
        updateSubTask(taskId); // Use the stored task ID
      }

      setResponses([]);
      setQuantity(1);
      setSelectedDate(currentDate);
      onClose();
    } catch (error) {
      console.error("Error submitting responses:", error);
    }
  };

  // Filter locations: Only show those where `completed_by` is NULL
  const availableLocations = filteredData
    .filter((item) => !item.completed_by) // Exclude locations with `completed_by` set
    .map((item) => item.location); // Extract locations

  if (loading) {
    return <Loading />;
  }

  return (
    <Box sx={{ p: 4, m: 2 }}>
      <Chip
        variant='contained'
        sx={{ fontSize: "14px", backgroundColor: "#F2F6FF", color: "#1751D0" }}
        label='Weekly Tasks'
      />

      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold", mb: 1 }}>
        Weekly First Aid Item Check List
      </Typography>

      <Typography variant='subtitle2' sx={{ color: "#757575", mb: 2 }}>
        Date: {currentDate} &nbsp; • &nbsp;
        <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>

      <Grid container spacing={2} mb={3}>
        <Grid item sm={3}>
          <FormControl fullWidth sx={{ height: "10px" }}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>Location</Typography>
            <Select value={location} onChange={(e) => setLocation(e.target.value)} displayEmpty size='small'>
              {availableLocations.length > 0 ? (
                availableLocations.map((loc, index) => (
                  <MenuItem key={index} value={loc}>
                    {loc}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>Already firstAids checked</MenuItem> // Handle case where no locations are available
              )}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {firstAids.length > 0 ? (
        <Grid container spacing={3}>
          <Grid item xs={6}>
            {firstAids.map((firstAid, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #E0E0E0",
                  py: 1,
                }}
              >
                <Typography sx={{ flex: 1, color: "black", fontSize: "14px" }}>
                  {`${index + 1} ${typeof firstAid === "object" ? firstAid.firstAid || "Unknown Item" : firstAid}`}
                </Typography>

                <IconButton
                  color='primary'
                  sx={{
                    color: responses.find((response) => response.firstAid === firstAid) ? "#1751D0" : "#E0E0E0",
                  }}
                >
                  <CheckCircleIcon />
                </IconButton>
              </Box>
            ))}
          </Grid>

          <Grid item xs={6}>
            <Grid container>
              <Grid item lg={6} md={12}>
                <Typography sx={{ fontSize: "14px", mb: 1 }}>Item</Typography>
                <Typography sx={{ fontSize: "14px", mb: 2, color: "black" }}>
                  {firstAids[currentProductIndex]}
                </Typography>
              </Grid>
              <Grid item lg={6} md={12}>
                <Button
                  variant='outlined'
                  startIcon={<ShoppingCartOutlinedIcon />}
                  sx={{
                    borderRadius: "50px", // Rounded edges
                    borderColor: "#2979ff", // Border color (blue)
                    color: "#4a4a4a", // Text color
                    fontWeight: "50",
                    textTransform: "none", // Prevent uppercase text
                    padding: "8px 20px",
                    fontSize: "12px",
                    "&:hover": {
                      borderColor: "#1a73e8",
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  Add to shopping list
                </Button>
              </Grid>
            </Grid>

            <Typography sx={{ fontSize: "14px", mb: 1 }}>Quantity</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Button variant='outlined' onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}>
                -
              </Button>
              <Typography>{quantity}</Typography>
              <Button variant='outlined' onClick={() => setQuantity((prev) => prev + 1)}>
                +
              </Button>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Typography sx={{ fontSize: "20px", color: "black", fontWeight: "bold", mt: 5 }}>
          There are no First Aids to display...
        </Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
        {currentProductIndex === 0 && (
          <Button variant='outlined' color='primary' onClick={() => onClose()}>
            Cancel
          </Button>
        )}
        {firstAids.length > 0 && (
          <>
            {currentProductIndex > 0 && (
              <Button variant='outlined' color='primary' onClick={() => setCurrentProductIndex((prev) => prev - 1)}>
                Previous
              </Button>
            )}
            {currentProductIndex < firstAids.length - 1 ? (
              <Button variant='contained' color='primary' onClick={handleNext} disabled={quantity === 0}>
                Next
              </Button>
            ) : (
              <Button variant='contained' color='primary' onClick={handleSubmit} disabled={quantity === 0}>
                Submit
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default WeeklyFirstAidItemCheck;
